import { I18nPluralPipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import {
  lucideChevronDown,
  lucideEllipsisVertical,
  lucideInfo,
  lucidePen,
  lucidePlus,
  lucideTrash,
} from '@ng-icons/lucide';
import { BrnPopoverImports } from '@spartan-ng/brain/popover';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { toast } from 'ngx-sonner';
import { filter } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../cartographie/models/cartographie.model';
import { ConfirmDialogComponent } from '../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ExpansionPanelMenuAction } from '../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '../shared/pipes/trier-par.pipe';
import { FormFormationObligatoireReglementaireComponent } from './form-formation-obligatoire-reglementaire/form-formation-obligatoire-reglementaire.component';
import { FormationObligatoireReglementaire } from './models/formation.model';
import { FormationReglementaireObligatoireStore } from './store/for.store';
import { PaginateurComponent } from '@shared/components/paginateur/paginateur.component';

@Component({
  selector: 'app-formation-obligatoire-reglementaire',
  imports: [
    HlmCardImports,
    HlmIconImports,
    TrierParPipe,
    HlmButtonImports,
    HlmPopoverImports,
    BrnPopoverImports,
    HlmTableImports,
    HlmDropdownMenuImports,
    HlmItemImports,
    I18nPluralPipe,
    PaginateurComponent,
  ],
  templateUrl: './formation-obligatoire-reglementaire.component.html',
  providers: [
    provideIcons({ lucidePlus, lucideEllipsisVertical, lucideInfo, lucideTrash, lucidePen, lucideChevronDown }),
  ],
})
export class FormationObligatoireReglementaireComponent {
  readonly store = inject(FormationReglementaireObligatoireStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);

  readonly cartographie = input.required<Cartographie>();

  readonly formations = computed(() => this.store.entities().map((x) => ({ ...x, expanded: false })));

  protected readonly anneeMapping: { [k: string]: string } = {
    '=0': '# an',
    '=1': '# an',
    other: '# ans',
  };

  readonly formationActions: ExpansionPanelMenuAction<FormationObligatoireReglementaire>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier la formation',
      action: (formation) => this.modifierFormation(formation),
    },
    {
      icon: 'lucideTrash',
      label: 'Supprimer la formation',
      action: (formation) => this.supprimerFormation(formation),
    },
  ];

  constructor() {
    effect(() => this.store.charger(this.cartographie().id));
  }

  ajouterFormation() {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une formation obligatoire règlementaire',
      FormFormationObligatoireReglementaireComponent,
      `Créez une nouvelle formation obligatoire réglementaire. Les utilisateurs pourront l’associer à une étape du parcours durant la modularisation.`,
      { idCartographie: this.cartographie().id },
    );
    this.#sidepanelService.open();
  }

  modifierFormation(formation?: FormationObligatoireReglementaire) {
    if (!formation) return;

    this.#sidepanelService.setComponentToDisplay(
      'Modifier une formation obligatoire règlementaire',
      FormFormationObligatoireReglementaireComponent,
      `Vous pouvez modifier les informations renseignées pour la formation.`,
      { idCartographie: this.cartographie().id, formation },
    );
    this.#sidepanelService.open();
  }

  supprimerFormation(formation?: FormationObligatoireReglementaire) {
    if (!formation) return;

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer la formation obligatoire règlementaire`,
        contenu: `Êtes-vous sûr de vouloir supprimer la formation obligatoire règlementaire <b>${formation.nom}</b> de la liste ?<br /><br />La formation sera retirée des étapes du parcours qui l’utilisent, et elle ne pourra plus être ajoutée à de nouvelles étapes. Elle sera supprimée de la cartographie actuelle, <b>${this.cartographie().nom}</b>.`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });
    const toastrId = uuidv7();

    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((result) => result),
      )
      .subscribe(() => {
        this.store.supprimer({
          id: formation.id,
          next: () =>
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: 'Suppression de la formation réussie', id: toastrId },
              id: toastrId,
            }),
        });
      });
  }
}
